import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Container, Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

const TYPE_LABELS = {
  application_submitted: 'application submitted confirmations',
  application_status_update: 'application status updates',
  job_alert: 'job alerts',
  saved_job_expiring: 'saved job expiry reminders',
  new_application_received: 'new application notifications',
  anti_ghosting_reminder: 'anti-ghosting reminders',
  listing_expiring: 'listing expiry reminders',
  package_expiring: 'package expiry reminders',
  refund_processed: 'refund notifications',
  admin_daily_summary: 'admin daily summaries',
  all: 'all marketing emails',
};

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const type = searchParams.get('type') || '';
  const [status, setStatus] = useState('loading');
  const [label, setLabel] = useState('');

  const apiBase = useMemo(
    () => (process.env.REACT_APP_API_URL || '').replace(/\/$/, ''),
    []
  );

  useEffect(() => {
    let cancelled = false;

    async function unsubscribe() {
      if (!apiBase || !token || !type) {
        if (!cancelled) setStatus('error');
        return;
      }

      setStatus('loading');
      try {
        const res = await fetch(`${apiBase}/api/v1/unsubscribe`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token, type }),
        });

        const data = await res.json().catch(() => ({}));

        if (!cancelled) {
          if (res.ok) {
            setLabel(data.data?.label || TYPE_LABELS[type] || type);
            setStatus('success');
          } else {
            setStatus('error');
          }
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    }

    unsubscribe();

    return () => {
      cancelled = true;
    };
  }, [apiBase, token, type]);

  const typeLabel = label || TYPE_LABELS[type] || 'these emails';

  return (
    <Container className="py-5" data-testid="rwm-unsubscribe-page">
      <div className="mx-auto" style={{ maxWidth: 520 }}>
        <h1 className="h3 mb-4">Email Preferences</h1>

        {status === 'loading' ? (
          <div className="d-flex align-items-center gap-2 text-muted" data-testid="rwm-unsubscribe-loading">
            <Spinner animation="border" size="sm" />
            <span>Updating your preferences…</span>
          </div>
        ) : null}

        {status === 'success' && type === 'all' ? (
          <Alert variant="success" data-testid="rwm-unsubscribe-success">
            You have been unsubscribed from all marketing emails. You can manage all email
            preferences in your dashboard.
          </Alert>
        ) : null}

        {status === 'success' && type !== 'all' ? (
          <Alert variant="success" data-testid="rwm-unsubscribe-success">
            You have been unsubscribed from {typeLabel}. You can manage all email preferences in
            your dashboard.
          </Alert>
        ) : null}

        {status === 'error' ? (
          <Alert variant="danger" data-testid="rwm-unsubscribe-error">
            Invalid or expired unsubscribe link.
          </Alert>
        ) : null}
      </div>
    </Container>
  );
}
